<div
  class="rounded-[16px] overflow-hidden border shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
  [ngClass]="[
    nested() ? 'bg-white' : 'bg-[#fefefe]',
    missingFieldsCount() > 0 ? 'border-red-500' : isExpanded() ? 'border-[#CBD5E1]' : 'border-transparent'
  ]"
>
  <ng-template #actionsTpl>
    <div class="flex items-center gap-3 flex-shrink-0">
      @if (showDeleteIcon()) {
        <button
          type="button"
          class="flex items-center justify-center text-red-400 transition-colors bg-transparent border-none p-0"
          aria-label="Remove product"
          (click)="remove(); $event.stopPropagation()"
        >
          <i class="pi pi-trash text-base"></i>
        </button>
      }
      @if (showEditIcon()) {
        <button
          type="button"
          class="flex items-center justify-center text-sky-500 transition-colors bg-transparent border-none p-0"
          aria-label="Edit product"
          (click)="onEdit($event)"
        >
          <i class="pi pi-file-edit text-base"></i>
        </button>
      }
      <button
        type="button"
        class="flex items-center justify-center text-secondary-500 transition-colors bg-transparent border-none p-0"
        [attr.aria-expanded]="isExpanded()"
        aria-label="Toggle product details"
        (click)="toggleExpanded($event)"
      >
        <i class="pi text-base" [class.pi-chevron-down]="!isExpanded()" [class.pi-chevron-up]="isExpanded()"></i>
      </button>
    </div>
  </ng-template>

  <ng-template #tagsTpl>
    @if (isKit()) {
      <p-tag value="KIT" severity="info" styleClass="text-body-2xs-600" />
    }
    @if (product().type && product().type?.toLowerCase() !== 'kit') {
      <p-tag [value]="product().type" severity="success" styleClass="text-body-2xs-600" />
    }
  </ng-template>

  <!-- Qty stepper; `compact` (pinned cards) is 20px tall with 12px text, otherwise 32px -->
  <ng-template #quantityTpl let-compact="compact">
    @if (quantityEditable()) {
      <div
        class="inline-flex items-center rounded-full border border-solid border-[#314022]"
        [ngClass]="compact ? 'h-5 px-0.5' : 'h-8 px-1'"
        (click)="$event.stopPropagation()"
      >
        <button
          type="button"
          class="flex items-center justify-center bg-transparent border-none p-0 cursor-pointer text-[#314022] disabled:opacity-40 disabled:cursor-default"
          [ngClass]="compact ? 'w-4 h-4' : 'w-7 h-7'"
          aria-label="Decrease quantity"
          [disabled]="values().quantity <= 1"
          (click)="changeQuantity(-1)"
        >
          <i class="pi pi-minus" [ngClass]="compact ? 'text-[9px]' : 'text-sm'"></i>
        </button>
        <span
          class="text-center text-[#314022]"
          [ngClass]="compact ? 'min-w-6 text-[12px] leading-none font-medium' : 'min-w-8 text-body-lg-500'"
          >{{ values().quantity || 0 }}</span
        >
        <button
          type="button"
          class="flex items-center justify-center bg-transparent border-none p-0 cursor-pointer text-[#314022]"
          [ngClass]="compact ? 'w-4 h-4' : 'w-7 h-7'"
          aria-label="Increase quantity"
          (click)="changeQuantity(1)"
        >
          <i class="pi pi-plus" [ngClass]="compact ? 'text-[9px]' : 'text-sm'"></i>
        </button>
      </div>
    } @else {
      <span
        class="flex items-center text-[#314022]"
        [ngClass]="compact ? 'h-5 text-[12px] font-medium' : 'h-8 text-body-lg-500'"
        >{{ values().quantity || 0 }}</span
      >
    }
  </ng-template>

  @if (inPopup()) {
    <!-- Popup (unpinned) layout: a single row with qty stepper, prices and actions -->
    <div class="flex items-center gap-2.5 p-4">
      <div
        class="w-[74px] h-[74px] flex-shrink-0 rounded-[16px] overflow-hidden bg-surface-100"
        [ngClass]="galleryImages().length ? 'cursor-pointer' : ''"
        (click)="openImageGallery($event)"
      >
        <img [src]="primaryImageUrl()" alt="Product image" class="w-full h-full object-cover" />
      </div>

      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-body-lg-600 !leading-none text-surface-700 truncate">{{ product().code }}</span>
          <ng-container [ngTemplateOutlet]="tagsTpl" />
          @if (missingFieldsCount() > 0) {
            <p-tag severity="danger" [value]="missingFieldsLabel()" styleClass="text-body-2xs-700" />
          }
        </div>
        <span class="text-body-sm text-surface-500 truncate">
          {{ product().category || '—' }} / {{ product().subcategory || '—' }}
        </span>
        <div class="flex items-center gap-4 min-w-0 text-body-sm text-surface-500">
          <span class="inline-flex items-center gap-1 shrink-0">
            <i class="pi pi-map-marker text-[12px] text-amber-600"></i>
            <span class="text-body-sm-600">{{ locationLabel() }}</span>
          </span>
          @if (attributeSummary()) {
            <span class="truncate">{{ attributeSummary() }}</span>
          }
        </div>
      </div>

      <!-- Hidden while expanded; the details below show qty and prices -->
      @if (!isExpanded()) {
        <div class="flex items-center gap-8 flex-shrink-0">
          <div class="flex flex-col items-center gap-1">
            <span class="text-[12px] uppercase tracking-wide text-surface-500">Quantity</span>
            <ng-container [ngTemplateOutlet]="quantityTpl" />
          </div>

          <div class="flex flex-col items-center gap-1">
            <span class="text-[12px] uppercase tracking-wide text-surface-500">Unit Price</span>
            <span class="h-8 flex items-center gap-1 text-body-lg-500 text-[#314022] whitespace-nowrap">
              @if (hasDiscount()) {
                <span class="text-surface-400 line-through">{{ values().unitPrice | currencyFormat }}</span>
                {{ discountedUnitPrice() | currencyFormat }}
              } @else {
                {{ values().unitPrice | currencyFormat }}
              }
            </span>
          </div>

          <div class="flex flex-col items-center gap-1">
            <span class="text-[12px] uppercase tracking-wide text-surface-500">Subtotal</span>
            <span class="h-8 flex items-center gap-1 text-body-lg-700 text-emerald-700 whitespace-nowrap">
              @if (hasDiscount()) {
                <span class="text-surface-400 line-through font-normal">{{ totalPrice() | currencyFormat }}</span>
                {{ discountedTotalPrice() | currencyFormat }}
              } @else {
                {{ totalPrice() | currencyFormat }}
              }
            </span>
          </div>
        </div>

        <div class="w-px h-8 bg-surface-300 flex-shrink-0 mx-2"></div>
      }

      <ng-container [ngTemplateOutlet]="actionsTpl" />
    </div>
  } @else {
    <div class="flex items-start gap-1.5 p-3">
      <div
        class="w-12 h-12 flex-shrink-0 rounded-[16px] overflow-hidden bg-surface-100"
        [ngClass]="galleryImages().length ? 'cursor-pointer' : ''"
        (click)="openImageGallery($event)"
      >
        <img [src]="primaryImageUrl()" alt="Product image" class="w-full h-full object-cover" />
      </div>

      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-body-lg-600 !leading-none text-surface-700 truncate">{{ product().code }}</span>
          <ng-container [ngTemplateOutlet]="tagsTpl" />
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-body-sm text-surface-500 truncate">
            {{ product().category || '—' }} / {{ product().subcategory || '—' }}
          </span>
          <div class="flex flex-wrap gap-x-4 gap-y-1.5 text-body-xs text-surface-500">
            @if (nested()) {
              <span
                >Location: <span class="text-body-xs-700">{{ locationLabel() }}</span></span
              >
            } @else {
              <span class="inline-flex items-center gap-1 text-body-sm-600 text-surface-500">
                <i class="pi pi-map-marker text-[12px] text-amber-600"></i>
                {{ locationLabel() }}
              </span>
            }
            @for (row of attributeRows(); track row.label) {
              <span
                >{{ row.label }}: <span class="text-body-xs-700">{{ row.value }}</span></span
              >
            }
            @if (nested()) {
              <span
                >Qty: <span class="text-body-xs-700">{{ values().quantity || 0 }}</span></span
              >
            }
          </div>
          @if (!nested()) {
            @if (missingFieldsCount() > 0) {
              <div>
                <p-tag severity="danger" [value]="missingFieldsLabel()" styleClass="text-body-2xs-700" />
              </div>
            }
          } @else {
            <div class="flex items-center gap-x-4 flex-wrap">
              <span class="text-body-xs text-surface-500"
                >Unit Price:
                @if (hasDiscount()) {
                  <span class="text-body-xs text-surface-400 line-through">{{
                    values().unitPrice | currencyFormat
                  }}</span>
                  <span class="text-body-xs-700 ml-1">{{ discountedUnitPrice() | currencyFormat }}</span>
                } @else {
                  <span class="text-body-xs-700">{{ values().unitPrice | currencyFormat }}</span>
                }
              </span>
              <span class="text-body-xs text-surface-500"
                >Total Cost:
                @if (hasDiscount()) {
                  <span class="text-body-xs text-surface-400 line-through">{{ totalPrice() | currencyFormat }}</span>
                  <span class="text-body-xs-700 ml-1">{{ discountedTotalPrice() | currencyFormat }}</span>
                } @else {
                  <span class="text-body-xs-700">{{ totalPrice() | currencyFormat }}</span>
                }
              </span>
              @if (missingFieldsCount() > 0) {
                <p-tag severity="danger" [value]="missingFieldsLabel()" styleClass="text-body-2xs-700" />
              }
            </div>
          }
        </div>
      </div>

      <ng-container [ngTemplateOutlet]="actionsTpl" />
    </div>

    @if (!nested() && !isExpanded()) {
      <!-- Pinned layout footer: unit price, subtotal and qty stepper (hidden while expanded; details show them) -->
      <div class="flex items-center justify-between gap-3 px-3 pb-3">
        <div class="flex items-baseline gap-2 min-w-0 flex-wrap">
          <span class="text-[14px] text-emerald-700 whitespace-nowrap">
            @if (hasDiscount()) {
              <span class="text-surface-400 line-through">{{ values().unitPrice | currencyFormat }}</span>
              {{ discountedUnitPrice() | currencyFormat }}
            } @else {
              {{ values().unitPrice | currencyFormat }}
            }
            ea
          </span>
          <span class="text-[14px] font-bold text-emerald-700 whitespace-nowrap">
            @if (hasDiscount()) {
              <span class="text-surface-400 line-through font-normal">{{ totalPrice() | currencyFormat }}</span>
              {{ discountedTotalPrice() | currencyFormat }}
            } @else {
              {{ totalPrice() | currencyFormat }}
            }
          </span>
        </div>
        <ng-container [ngTemplateOutlet]="quantityTpl" [ngTemplateOutletContext]="{ compact: true }" />
      </div>
    }
  }

  @if (isExpanded()) {
    <div class="p-4">
      @if (isKit() && !nested()) {
        <p-buttonGroup>
          <p-button
            label="Option Setup"
            type="button"
            size="small"
            [severity]="activeKitTab() === 'setup' ? 'primary' : 'secondary'"
            (onClick)="activeKitTab.set('setup'); $event.stopPropagation()"
          />
          <p-button
            label="Linked Options"
            [badge]="linkedPanelViews().length.toString()"
            type="button"
            size="small"
            [severity]="activeKitTab() === 'linked' ? 'primary' : 'secondary'"
            (onClick)="activeKitTab.set('linked'); $event.stopPropagation()"
          />
        </p-buttonGroup>
      }

      <!-- Option Setup (the option's / kit's own configuration) -->
      <div [class.hidden]="isKit() && !nested() && activeKitTab() !== 'setup'">
        @if (editable() && isEditingInline()) {
          <div class="mt-3 flex flex-col gap-3" (click)="$event.stopPropagation()">
            <dsm-selected-product-config-form
              #inlineConfigForm
              [product]="product()"
              [initialValues]="values()"
              [addendumId]="addendumId()"
              [lotId]="lotId()"
              (commit)="onInlineCommit($event)"
            />
            <div class="flex items-center justify-end gap-2">
              <p-button label="Cancel" severity="secondary" size="small" type="button" (onClick)="cancelInlineEdit()" />
              <p-button label="Save" size="small" type="button" (onClick)="confirmInlineEdit()" />
            </div>
          </div>
        } @else {
          <div class="grid grid-cols-2 gap-5 mt-3">
            <div>
              <span class="text-xs font-semibold uppercase tracking-wide text-surface-500">Location</span>
              <p class="m-0 mt-0.5 text-body-500 text-surface-700">{{ locationLabel() }}</p>
            </div>

            @for (row of attributeRows(); track row.label) {
              <div>
                <span class="text-xs uppercase tracking-wide text-surface-700">{{ row.label }}</span>
                <p class="m-0 mt-0.5 text-body-500 text-surface-700">{{ row.value }}</p>
              </div>
            }

            <div>
              <span class="text-body-xs uppercase tracking-wide text-surface-700">Unit Price</span>
              <p class="m-0 mt-0.5 text-body-500 text-surface-700">
                @if (hasDiscount()) {
                  <span class="text-surface-400 line-through">{{ values().unitPrice | currencyFormat }}</span>
                  <span class="ml-1">{{ discountedUnitPrice() | currencyFormat }}</span>
                } @else {
                  {{ values().unitPrice | currencyFormat }}
                }
                @if (product().uom) {
                  <span class="text-surface-700">/ {{ product().uom }}</span>
                }
              </p>
            </div>

            <div>
              <span class="text-body-xs uppercase tracking-wide text-surface-700">Quantity</span>
              <p class="m-0 mt-0.5 text-body-500 text-surface-700">
                {{ values().quantity || 0 }}
              </p>
            </div>

            <div>
              <span class="text-body-xs uppercase tracking-wide text-surface-700">Total Cost</span>
              <p class="m-0 mt-0.5 text-body-500 text-surface-700">
                @if (hasDiscount()) {
                  <span class="text-surface-400 line-through">{{ totalPrice() | currencyFormat }}</span>
                  <span class="ml-1">{{ discountedTotalPrice() | currencyFormat }}</span>
                } @else {
                  {{ totalPrice() | currencyFormat }}
                }
              </p>
            </div>

            <div class="col-span-2">
              <span class="text-body-xs uppercase tracking-wide text-surface-700">Sales Description</span>
              <p class="m-0 mt-0.5 text-body-500 text-surface-700">{{ product().salesDescription || '—' }}</p>
            </div>

            <div class="col-span-2">
              <span class="text-body-xs uppercase tracking-wide text-surface-700">Option Notes</span>
              <p class="m-0 mt-0.5 text-body-sm text-surface-700 whitespace-pre-wrap">{{ notesDisplay() }}</p>
            </div>
          </div>
        }
      </div>

      <!-- Linked Options (a kit's linked products as nested read-only cards) -->
      @if (isKit() && !nested()) {
        <div [class.hidden]="activeKitTab() !== 'linked'" class="mt-3 flex flex-col gap-3">
          @if (!linkedPanelViews().length) {
            <p class="text-body-sm text-surface-500 m-0">This kit has no linked products.</p>
          } @else {
            @for (linked of linkedPanelViews(); track linked.product.modelElevationProductsId; let i = $index) {
              <dsm-selected-product-panel
                [addendumId]="addendumId()"
                [lotId]="lotId()"
                [product]="linked.product"
                [values]="linked.values"
                [attributeGroups]="linked.attributes"
                [showEditIcon]="true"
                [showDeleteIcon]="false"
                [nested]="true"
                (editRequested)="openLinkedEdit(i)"
              />
            }
          }
        </div>
      }
    </div>
  }
</div>

<p-dialog
  [visible]="editingLinkedIndex() !== null"
  (visibleChange)="onLinkedDialogVisibility($event)"
  [modal]="true"
  [header]="editingLinkedView()?.product?.code ?? ''"
  [style]="{ width: '32rem', maxWidth: '95vw' }"
  [contentStyle]="{ overflowY: 'auto', maxHeight: 'calc(80vh - 130px)' }"
  position="top"
  [draggable]="false"
  [resizable]="false"
  [baseZIndex]="12000"
  appendTo="body"
>
  @if (editingLinkedView(); as linkedView) {
    <dsm-selected-product-config-form
      #linkedConfigForm
      [product]="linkedView.product"
      [initialValues]="linkedView.values"
      [addendumId]="addendumId()"
      [lotId]="lotId()"
      (commit)="onLinkedEditCommit($event)"
    />
  }
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" type="button" (onClick)="closeLinkedEdit()" />
    <p-button label="Save" type="button" (onClick)="confirmLinkedEdit()" />
  </ng-template>
</p-dialog>

<p-galleria
  [value]="galleryImages()"
  [(visible)]="imageGalleryVisible"
  [responsiveOptions]="[]"
  [containerStyle]="{ width: '100vw', maxWidth: '960px' }"
  [numVisible]="5"
  [circular]="true"
  [showItemNavigators]="true"
  [showThumbnails]="true"
  [fullScreen]="true"
  [activeIndex]="activeImageIndex()"
  [showIndicators]="galleryImages().length > 1"
>
  <ng-template pTemplate="item" let-item>
    <img [src]="item.itemImageSrc" [alt]="item.alt" style="width: 100%; display: block" />
  </ng-template>
  <ng-template pTemplate="thumbnail" let-item>
    <img [src]="item.thumbnailImageSrc" [alt]="item.alt" style="display: block; width: 100%" />
  </ng-template>
</p-galleria>
